import {
  Image,
  PictureAsPdf,
  InsertDriveFile,
  Download,
  OpenInNew,
  VideoFile,
} from '@mui/icons-material';
import { Alert, CircularProgress, IconButton, Box, Snackbar } from '@mui/material';
import React, { useState, useMemo, useCallback } from 'react';

import { MobileAttachmentsProps, AttachmentFilterType } from './MobileAttachments.props';
import {
  FilterTabs,
  FilterTab,
  AttachmentList,
  AttachmentItem,
  AttachmentIcon,
  AttachmentInfo,
  AttachmentName,
  AttachmentMeta,
  AttachmentActions,
  EmptyState,
  LoadingContainer,
  ImagePreview,
  ContentWrapper,
} from './MobileAttachments.styles';
import { UploadedFile } from '../../../../../@types/uploaded-files.type';
import { MobileImageViewer } from '../../../../../components/molecules/MobileImageViewer';
import type { ImageAttachment } from '../../../../../components/molecules/MobileImageViewer';
import { COLORS } from '../../../../../configs/colors';
import { useGetUploadedFilePresignedUrlById } from '../../../../../queries/uploadedfiles-query';
import { formatDate, isSafeUrl } from '../../../../../utils/common';

const isImageMimeType = (mimeType: string): boolean => {
  return mimeType.startsWith('image/');
};

const isVideoMimeType = (mimeType: string): boolean => {
  return mimeType.startsWith('video/');
};

const isPdfMimeType = (mimeType: string): boolean => {
  return mimeType === 'application/pdf';
};

const getFileIcon = (mimeType: string): React.ReactNode => {
  if (isImageMimeType(mimeType)) return <Image />;
  if (isVideoMimeType(mimeType)) return <VideoFile />;
  if (isPdfMimeType(mimeType)) return <PictureAsPdf />;
  return <InsertDriveFile />;
};

export const MobileAttachments: React.FC<MobileAttachmentsProps> = ({
  attachments = [],
  isLoading,
}) => {
  const [filter, setFilter] = useState<AttachmentFilterType>('all');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const imageAttachments: ImageAttachment[] = useMemo(
    () =>
      attachments
        .filter((a) => isImageMimeType(a.mimeType))
        .map((a) => ({ id: a.id, url: a.url, fileName: a.fileName })),
    [attachments]
  );

  const handleImageTap = useCallback(
    (attachmentId: number): void => {
      const index = imageAttachments.findIndex((img) => img.id === attachmentId);
      if (index >= 0) {
        setViewerInitialIndex(index);
        setViewerOpen(true);
      }
    },
    [imageAttachments]
  );

  const handleViewerClose = useCallback((): void => {
    setViewerOpen(false);
  }, []);

  const filteredAttachments = useMemo(() => {
    if (filter === 'all') return attachments;
    if (filter === 'images') {
      return attachments.filter((a) => isImageMimeType(a.mimeType) || isVideoMimeType(a.mimeType));
    }
    // files = PDFs, Word docs, Excel, etc.
    return attachments.filter((a) => !isImageMimeType(a.mimeType) && !isVideoMimeType(a.mimeType));
  }, [attachments, filter]);

  const counts = useMemo(() => {
    const images = attachments.filter(
      (a) => isImageMimeType(a.mimeType) || isVideoMimeType(a.mimeType)
    ).length;
    const files = attachments.length - images;
    return { all: attachments.length, images, files };
  }, [attachments]);

  if (isLoading) {
    return (
      <Box sx={{ padding: '16px', backgroundColor: COLORS.White }}>
        <LoadingContainer>
          <CircularProgress size={24} />
        </LoadingContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: COLORS.White }}>
      <FilterTabs role="group" aria-label="Filter attachments">
        <FilterTab
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          aria-pressed={filter === 'all'}
        >
          All ({counts.all})
        </FilterTab>
        <FilterTab
          active={filter === 'images'}
          onClick={() => setFilter('images')}
          aria-pressed={filter === 'images'}
        >
          Images ({counts.images})
        </FilterTab>
        <FilterTab
          active={filter === 'files'}
          onClick={() => setFilter('files')}
          aria-pressed={filter === 'files'}
        >
          Files ({counts.files})
        </FilterTab>
      </FilterTabs>

      <ContentWrapper>
        {filteredAttachments.length === 0 ? (
          <EmptyState>
            {attachments.length === 0
              ? 'No attachments'
              : `No ${filter === 'images' ? 'images' : 'files'} found`}
          </EmptyState>
        ) : (
          <AttachmentList>
            {filteredAttachments.map((attachment) => (
              <AttachmentRow
                key={attachment.id}
                attachment={attachment}
                onImageTap={handleImageTap}
                onError={setErrorMessage}
              />
            ))}
          </AttachmentList>
        )}
      </ContentWrapper>

      <MobileImageViewer
        open={viewerOpen}
        images={imageAttachments}
        initialIndex={viewerInitialIndex}
        onClose={handleViewerClose}
      />

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)} variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface AttachmentRowProps {
  attachment: UploadedFile;
  onImageTap: (attachmentId: number) => void;
  onError: (message: string) => void;
}

const AttachmentRow: React.FC<AttachmentRowProps> = ({ attachment, onImageTap, onError }) => {
  const getPresignedUrl = useGetUploadedFilePresignedUrlById(attachment.id, 'inline');
  const getDownloadUrl = useGetUploadedFilePresignedUrlById(attachment.id, 'attachment');
  const isImage = isImageMimeType(attachment.mimeType);

  const handleView = async (): Promise<void> => {
    if (isImage) {
      onImageTap(attachment.id);
      return;
    }
    try {
      const result = await getPresignedUrl.refetch();
      if (result.data?.url && isSafeUrl(result.data.url)) {
        window.open(result.data.url, '_blank');
      } else {
        onError(`Could not open "${attachment.fileName}"`);
      }
    } catch {
      onError(`Failed to open "${attachment.fileName}"`);
    }
  };

  const handleDownload = async (): Promise<void> => {
    try {
      const result = await getDownloadUrl.refetch();
      if (result.data?.url && isSafeUrl(result.data.url)) {
        const link = document.createElement('a');
        link.href = result.data.url;
        link.download = attachment.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        onError(`Could not download "${attachment.fileName}"`);
      }
    } catch {
      onError(`Failed to download "${attachment.fileName}"`);
    }
  };

  const handleThumbnailTap = (): void => {
    if (isImage) {
      onImageTap(attachment.id);
    }
  };

  const handleThumbnailKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleThumbnailTap();
    }
  };

  return (
    <AttachmentItem>
      <AttachmentIcon
        onClick={handleThumbnailTap}
        onKeyDown={isImage ? handleThumbnailKeyDown : undefined}
        sx={{
          cursor: isImage ? 'pointer' : 'default',
          '&:focus-visible': isImage
            ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' }
            : {},
        }}
        role={isImage ? 'button' : undefined}
        tabIndex={isImage ? 0 : undefined}
        aria-label={isImage ? `View ${attachment.fileName}` : undefined}
      >
        {isImage && attachment.url ? (
          <ImagePreview src={attachment.url} alt={attachment.fileName} />
        ) : (
          getFileIcon(attachment.mimeType)
        )}
      </AttachmentIcon>

      <AttachmentInfo>
        <AttachmentName>{attachment.fileName}</AttachmentName>
        <AttachmentMeta>
          {attachment.createdBy} • {formatDate(attachment.createdAt)}
        </AttachmentMeta>
      </AttachmentInfo>

      <AttachmentActions>
        <IconButton
          size="small"
          onClick={handleView}
          disabled={!isImage && getPresignedUrl.isFetching}
          aria-label={isImage ? 'View image' : 'View attachment'}
        >
          <OpenInNew fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleDownload}
          disabled={getDownloadUrl.isFetching}
          aria-label="Download attachment"
        >
          <Download fontSize="small" />
        </IconButton>
      </AttachmentActions>
    </AttachmentItem>
  );
};
